import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GlobalLoadingScreen from './common/GlobalLoadingScreen';
import ScrollToTop from './common/ScrollToTop';
import AuthenticatedApp from './AuthenticatedApp';
import UnauthenticatedApp from './UnauthenticatedApp';
import PublicApp from './PublicApp';

/**
 * AuthGate - Centralized authentication state management and routing
 * 
 * This component acts as a gate that:
 * - Waits for authentication initialization to complete
 * - Determines authentication status once
 * - Routes to appropriate app components based on auth state
 * - Shows unified loading screen during initialization
 * - Handles auth initialization timeout and error states
 * 
 * Requirements addressed:
 * - 1.1: Skip login page for authenticated users
 * - 1.2: Show consistent loading indicator during auth determination
 * - 1.3: Smooth transitions without visual flash
 * - 3.1: Unified loading screen during initialization
 */
const AuthGate = () => {
  const location = useLocation();
  const { currentUser, isAuthReady, authError, loading } = useAuth();
  const [authStatus, setAuthStatus] = useState('initializing');
  const [initializationTimeout, setInitializationTimeout] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Check if current route is a public route
  // Note: Must check exact paths to avoid matching /contractors or /tenants (authenticated routes)
  const isPublicRoute = location.pathname.startsWith('/public/') || 
                         location.pathname === '/contractor' ||
                         location.pathname.startsWith('/contractor/') ||
                         location.pathname === '/tenant' ||
                         location.pathname.startsWith('/tenant/');

  // Maximum time to wait for auth initialization (5 seconds)
  const AUTH_TIMEOUT_MS = 5000;
  const MAX_RETRY_ATTEMPTS = 3;

  useEffect(() => {
    // Set up timeout for auth initialization
    const timeoutId = setTimeout(() => {
      if (!isAuthReady && authStatus === 'initializing') {
        console.warn('Auth initialization timeout reached');
        setInitializationTimeout(true);
      }
    }, AUTH_TIMEOUT_MS);

    // Determine auth status based on auth context state
    if (isAuthReady && !loading) {
      clearTimeout(timeoutId);

      if (authError) {
        // Check if this is a suspended account error
        // If so, treat as unauthenticated so LoginPage can handle it properly
        if (authError.message && authError.message.includes('SUSPENDED_ACCOUNT:')) {
          setAuthStatus('unauthenticated');
        } else {
          setAuthStatus('error');
        }
      } else if (currentUser) {
        setAuthStatus('authenticated');
      } else {
        setAuthStatus('unauthenticated');
      }
    }

    return () => clearTimeout(timeoutId);
  }, [isAuthReady, loading, currentUser, authError, authStatus]);

  // Handle retry mechanism for failed auth initialization
  const handleRetry = () => {
    if (retryCount < MAX_RETRY_ATTEMPTS) {
      setRetryCount(prev => prev + 1);
      setAuthStatus('initializing');
      setInitializationTimeout(false);
      
      // Force a page reload as a fallback retry mechanism
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      // Max retries reached, fall back to showing login
      setAuthStatus('unauthenticated');
    }
  };

  // Handle initialization timeout
  if (initializationTimeout && authStatus === 'initializing') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ backgroundColor: 'var(--color-background)', color: 'var(--color-text-primary)' }}>
        <div className="text-center max-w-md mx-auto p-6">
          <div className="mb-6">
            <div 
              className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4"
              style={{ borderColor: 'rgba(20, 255, 163, 0.3)', borderTopColor: 'var(--color-primary)' }}
            ></div>
            <h2 className="text-xl font-semibold mb-2">Connection Taking Longer Than Expected</h2>
            <p className="mb-4" style={{ color: 'var(--color-text-secondary)' }}>
              We're having trouble connecting to our servers. This might be due to a slow internet connection.
            </p>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={handleRetry}
              disabled={retryCount >= MAX_RETRY_ATTEMPTS}
              className="w-full px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              style={{ 
                backgroundColor: 'var(--color-primary)',
                ':hover': { backgroundColor: 'color-mix(in srgb, var(--color-primary), black 20%)' }
              }}
            >
              {retryCount >= MAX_RETRY_ATTEMPTS ? 'Max Retries Reached' : `Retry (${retryCount + 1}/${MAX_RETRY_ATTEMPTS})`}
            </button>
            
            <button
              onClick={() => setAuthStatus('unauthenticated')}
              className="w-full px-4 py-2 rounded-lg transition-colors hover:opacity-80"
              style={{ 
                border: '1px solid var(--color-border)', 
                color: 'var(--color-text-primary)',
                backgroundColor: 'transparent'
              }}
            >
              Continue to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Handle auth initialization errors
  if (authStatus === 'error') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ backgroundColor: 'var(--color-background)', color: 'var(--color-text-primary)' }}>
        <div className="text-center max-w-md mx-auto p-6">
          <div className="mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold mb-2">Authentication Error</h2>
            <p className="mb-4" style={{ color: 'var(--color-text-secondary)' }}>
              There was a problem initializing the authentication system.
            </p>
            {authError && (
              <p className="text-sm text-red-600 mb-4 font-mono bg-red-50 p-2 rounded">
                {authError.message}
              </p>
            )}
          </div>
          
          <div className="space-y-3">
            <button
              onClick={handleRetry}
              disabled={retryCount >= MAX_RETRY_ATTEMPTS}
              className="w-full px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              style={{ 
                backgroundColor: 'var(--color-primary)',
                ':hover': { backgroundColor: 'color-mix(in srgb, var(--color-primary), black 20%)' }
              }}
            >
              {retryCount >= MAX_RETRY_ATTEMPTS ? 'Max Retries Reached' : `Try Again (${retryCount + 1}/${MAX_RETRY_ATTEMPTS})`}
            </button>
            
            <button
              onClick={() => setAuthStatus('unauthenticated')}
              className="w-full px-4 py-2 rounded-lg transition-colors hover:opacity-80"
              style={{ 
                border: '1px solid var(--color-border)', 
                color: 'var(--color-text-primary)',
                backgroundColor: 'transparent'
              }}
            >
              Continue to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading screen during auth initialization
  if (authStatus === 'initializing') {
    return (
      <GlobalLoadingScreen 
        message="Initializing application..."
        showLogo={true}
      />
    );
  }

  // Public routes don't require authentication - handle them first
  if (isPublicRoute) {
    return (
      <>
        <ScrollToTop />
        <PublicApp />
      </>
    );
  }

  // Route to appropriate app component based on auth status
  if (authStatus === 'authenticated') {
    return (
      <>
        <ScrollToTop />
        <AuthenticatedApp />
      </>
    );
  }
  
  if (authStatus === 'unauthenticated') {
    return (
      <>
        <ScrollToTop />
        <UnauthenticatedApp />
      </>
    );
  }
  
  // Fallback - should not reach here, but return loading screen just in case
  return (
    <GlobalLoadingScreen 
      message="Loading application..."
      showLogo={true}
    />
  );
};

export default AuthGate;